import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Changer les roulettes d\'une baie coulissante' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Changer les roulettes d\'une baie coulissante : quand et comment',
  description: 'Guide complet sur le changement des roulettes de baie coulissante : signes d\'usure, types de galets, dépose du vantail, et pourquoi faire appel à un professionnel dans l\'Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/changer-roulettes-baie-coulissante/',
  datePublished: '2026-07-15',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Comment savoir si les roulettes d\'une baie coulissante sont usées ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les signes sont : la baie devient dure à ouvrir ou fermer, elle émet un bruit de raclement ou de grincement, le vantail frotte sur le rail, ou la baie semble s\'affaisser d\'un côté. Si vous devez forcer pour la faire coulisser, les roulettes sont très probablement en fin de vie.',
      },
    },
    {
      '@type': 'Question',
      name: 'Peut-on changer soi-même les roulettes d\'une baie coulissante aluminium ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Techniquement, c\'est possible mais déconseillé. Le vantail d\'une baie coulissante pèse entre 80 et 150 kg. Sa dépose nécessite un outillage spécifique, de la précision, et présente un risque de casse du vitrage. De plus, les systèmes de roulettes varient selon les marques (Technal, Sepalumic, etc.) et un mauvais choix de pièce peut endommager le rail.',
      },
    },
    {
      '@type': 'Question',
      name: 'Combien de temps dure une intervention pour changer les roulettes ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Une intervention standard de changement de roulettes sur une baie coulissante à deux vantaux prend généralement entre 1h et 2h. Ce délai inclut la dépose des vantaux, le remplacement des galets, le nettoyage du rail, le réglage et la repose.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/changer-roulettes-baie-coulissante/', name: 'Changer les roulettes d\'une baie coulissante' } },
  ],
};

export default function ArticleRoulettesBaie() {
  return (
    <>
      <SEO
        title="Changer les roulettes d'une baie coulissante : guide"
        description="Guide complet : quand et comment changer les roulettes d'une baie coulissante. Signes d'usure, types de galets, dépose du vantail."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/changer-roulettes-baie-coulissante/"
      />
      <BlogArticleLayout
        heroImage="/images/blog-roulettes-baie.jpg"
        title="Changer les roulettes d&apos;une baie coulissante : quand et comment"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="15 juillet 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Une baie vitrée coulissante qui devient dure à manœuvrer, qui accroche ou qui émet un bruit de raclement désagréable : voilà le signe le plus évident que les roulettes — aussi appelées galets — arrivent en fin de vie. Dans une région comme l&apos;Hérault, où les baies coulissantes sont très répandues dans l&apos;habitat récent comme dans les résidences de bord de mer, l&apos;usure des roulettes est le problème numéro un que nous constatons lors de nos interventions à Montpellier, Sète, Agde ou La Grande-Motte.
        </p>

        <h2>À quoi servent les roulettes d&apos;une baie coulissante ?</h2>
        <p>
          Les roulettes sont de petits galets montés sur roulement, insérés dans la partie inférieure du vantail. Elles supportent l&apos;intégralité du poids du vitrage — qui peut dépasser les 120 kg pour un double vitrage grand format — et permettent au vantail de glisser le long du rail avec un minimum d&apos;effort. Avec le temps, la poussière, le sable, l&apos;humidité et le sel (particulièrement présents sur le littoral héraultais) s&apos;infiltrent dans le mécanisme, usent les roulements et finissent par bloquer la rotation des galets.
        </p>

        <h2>Les signes qui ne trompent pas</h2>
        <p>
          Plusieurs indices doivent vous alerter avant que la panne ne devienne critique. Le premier, c&apos;est la résistance à l&apos;ouverture : si vous devez exercer une force anormale pour faire coulisser votre baie, les roulettes ne tournent plus librement. Le deuxième, c&apos;est le bruit : un grincement métallique ou un raclement signale que le galet frotte contre le rail au lieu de rouler dessus. Le troisième indice est visuel : si le vantail semble s&apos;affaisser d&apos;un côté ou si l&apos;écart entre le bas du vantail et le rail n&apos;est pas uniforme, le réglage des roulettes est à reprendre — ou les roulettes sont tout simplement mortes.
        </p>

        <h2>Roulettes réglables ou à billes : quelles différences ?</h2>
        <p>
          Il existe deux grandes familles de roulettes pour baies coulissantes aluminium. Les roulettes réglables, équipées d&apos;une vis d&apos;excentrique, permettent d&apos;ajuster la hauteur du vantail pour compenser l&apos;usure ou un tassement du bâti. Les roulettes à billes simple rangée, plus anciennes, sont souvent montées sur des menuiseries de première génération. Certains fabricants comme Technal ou Sepalumic utilisent des galets spécifiques avec des axes et des diamètres propriétaires : il est impératif de choisir le bon modèle. Un galet incompatible ne s&apos;insérera pas correctement dans le logement prévu et pourra endommager le rail en quelques semaines.
        </p>

        {/* CTA milieu d'article */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Vos roulettes de baie coulissante sont usées dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            Nos techniciens remplacent vos galets sous 24 à 48h. Diagnostic et <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">devis gratuit</Link>.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Déposer un vantail de baie coulissante : une opération délicate</h2>
        <p>
          Le remplacement des roulettes commence par la dépose du vantail. Cette opération n&apos;est pas anodine. Un vantail de baie coulissante pèse entre 80 et 150 kg selon ses dimensions et l&apos;épaisseur du double vitrage. La manœuvre nécessite deux personnes, des ventouses de levage et une technique précise pour ne pas abîmer le rail ni faire basculer le vantail. Une fois le vantail couché sur des cales, on accède aux logements des roulettes situés dans le profilé inférieur. Les anciens galets sont extraits — parfois avec difficulté s&apos;ils sont grippés par la corrosion — puis remplacés par les nouveaux modèles, identiques ou compatibles.
        </p>

        <h2>Pourquoi confier le changement à un professionnel</h2>
        <p>
          Au-delà du poids du vantail, plusieurs raisons plaident pour une intervention professionnelle. D&apos;abord, l&apos;identification du bon modèle de roulettes : les catalogues de pièces détachées sont vastes, et un œil non averti peut commander un galet inadapté. Ensuite, le réglage post-intervention : une fois les roulettes remplacées, il faut réajuster la hauteur et l&apos;alignement des vantaux pour garantir un coulissement fluide et une parfaite étanchéité à l&apos;air et à l&apos;eau. Enfin, la sécurité : un vantail mal reposé peut dérailler et chuter, avec des conséquences potentiellement graves. C&apos;est pour toutes ces raisons que nous recommandons de faire appel à un réparateur qualifié, comme nos techniciens DFPRO Hérault, qui interviennent sur <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">tous types de baies coulissantes</Link> dans le département.
        </p>

        <h2>Les marques que nous réparons</h2>
        <p>
          Nous intervenons sur l&apos;ensemble des marques présentes sur le marché de l&apos;Hérault : Technal, très répandu dans l&apos;habitat récent et les résidences de standing, Sepalumic, fréquent dans les constructions des années 2000, mais aussi Schüco, K-Line, Wicona, Kawneer, ou encore SAPA. Chaque fabricant a développé son propre système de roulettes, et nous disposons d&apos;un stock de pièces couvrant les références les plus courantes, ce qui nous permet d&apos;intervenir rapidement, souvent le jour même du diagnostic.
        </p>

        <h2>Entretien préventif : prolonger la vie de vos roulettes</h2>
        <p>
          Une fois les roulettes changées, un entretien régulier permet d&apos;en prolonger considérablement la durée de vie. Nettoyez le rail de votre baie coulissante tous les trois à six mois à l&apos;aide d&apos;un aspirateur équipé d&apos;un embout fin, puis passez un chiffon légèrement humide pour retirer les résidus tenaces. Évitez les produits gras ou les lubrifiants en bombe, qui attirent la poussière et forment une pâte abrasive avec le temps. En bord de mer — Sète, Frontignan, La Grande-Motte — un rinçage à l&apos;eau claire du rail après les épisodes de vent marin permet de limiter les dépôts salins qui accélèrent la corrosion.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Combien de temps durent des roulettes de baie coulissante ?</h3>
        <p>
          La durée de vie des roulettes dépend de la qualité des galets, du poids du vantail, de la fréquence d&apos;utilisation et de l&apos;environnement. Dans l&apos;Hérault, en bord de mer, l&apos;air salin accélère l&apos;usure. Une baie bien entretenue peut garder ses roulettes dix ans ou plus ; une baie exposée au sable et au sel sans entretien peut nécessiter un changement au bout de cinq à sept ans.
        </p>

        <h3>Peut-on changer seulement une roulette sur deux ?</h3>
        <p>
          C&apos;est possible, mais rarement recommandé. Si une roulette est usée au point de devoir être remplacée, l&apos;autre a généralement subi le même niveau de sollicitation. Changer les deux en même temps garantit une usure homogène et un coulissement fluide. Nos techniciens évaluent l&apos;état de chaque galet lors du diagnostic et vous conseillent au cas par cas.
        </p>
      </BlogArticleLayout>
    </>
  );
}